import {
  defineAsyncComponent,
  markRaw,
  reactive,
  ref,
} from 'vue';
import { defineStore } from 'pinia';
import { v7 as uuidv7 } from 'uuid';

export default defineStore('componentStore', () => {
  const components = ref({});
  const alert = ref({});
  const confirm = ref({});
  const imageListDialog = reactive({});
  const imageRegisterDialog = reactive({});
  const templatesDialog = reactive({});
  const templatesRegisterDialog = reactive({});
  const buttonTemplateDialog = reactive({});
  const fallbackMessageDialog = reactive({});
  const couponTemplatesDialog = reactive({});
  const couponSettingDialog = reactive({});
  const couponTemplateRegisterDialog = reactive({});
  const carouselTailSettingDialog = reactive({});
  const alimtalkTemplatesDialog = reactive({});
  const alimtalkButtonDialog = reactive({});
  const reservationDialog = reactive({});
  // const deliveryResult = reactive({});
  const additionalFields = reactive({});
  const advertInfo = reactive({});
  const advertWarning = reactive({});
  const templatePreview = reactive({});
  const targetingSettings = reactive({});
  const brandTemplates = reactive({});

  const alertContentList = [];

  const addComponents = ({ component, id }) => {
    components.value[id] = markRaw(component);
  };

  const removeComponent = ({ id }) => {
    delete components.value[id];
  };

  // alert dialog
  const openAlertDialog = (options = {}) => {
    const alertId = uuidv7();

    if (alertContentList.includes(options.content)) {
      return {
        promise: new Promise((resolve) => {
          setTimeout(() => {
            resolve();
          }, 0);
        }),
      };
    }

    alertContentList.push(options.content);
    const componentModule = defineAsyncComponent(() => import('@/components/dialog/AlertDialog.vue'));

    const promise = new Promise((resolve, reject) => {
      alert.value[alertId] = {
        resolve,
        reject,
        options,
        state: true,
      };
    });

    addComponents({ component: componentModule, id: alertId });

    return {
      id: alertId,
      promise,
    };
  };

  const closeAlertDialog = ({ id }) => {
    const { content } = alert.value[id].options.content;
    const index = alertContentList.indexOf(content);

    alertContentList.splice(index, 1);

    removeComponent({ id });
  };

  defineAsyncComponent(() => import('@/components/dialog/ConfirmDialog.vue'));

  const openConfirmDialog = (options = {}) => {
    const id = uuidv7();

    const componentModule = defineAsyncComponent(() => import('@/components/dialog/ConfirmDialog.vue'));

    const promise = new Promise((resolve, reject) => {
      confirm.value[id] = {
        resolve,
        reject,
        options,
        state: true,
      };
    });

    addComponents({ component: componentModule, id });

    return {
      id,
      promise,
    };
  };

  const closeConfirmDialog = ({ id }) => {
    removeComponent({ id });
  };

  const openImageListDialog = (options = {}) => {
    if (!imageListDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/image/ImageListDialog.vue'));
      addComponents({ component: componentModule, id: 'imageListDialog' });
      imageListDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      imageListDialog.resolve = resolve;
      imageListDialog.reject = reject;
      imageListDialog.options = options;
      imageListDialog.state = true;
    });
  };

  const closeImageListDialog = () => {
    imageListDialog.state = false;
  };

  const openImageRegisterDialog = (options = {}) => {
    if (!imageRegisterDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/image/ImageRegister.vue'));
      addComponents({ component: componentModule, id: 'imageRegister' });
      imageRegisterDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      imageRegisterDialog.resolve = resolve;
      imageRegisterDialog.reject = reject;
      imageRegisterDialog.options = options;
      imageRegisterDialog.state = true;
    });
  };

  const closeImageRegisterDialog = () => {
    imageRegisterDialog.state = false;
  };

  const openTemplatesDialog = (options = {}) => {
    if (!templatesDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/templates/TemplatesDialog.vue'));
      addComponents({ component: componentModule, id: 'templatesDialog' });
      templatesDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      templatesDialog.resolve = resolve;
      templatesDialog.reject = reject;
      templatesDialog.options = options;
      templatesDialog.state = true;
    });
  };

  const closeTemplatesDialog = () => {
    templatesDialog.state = false;
  };

  const openTemplatesRegisterDialog = (options = {}) => {
    if (!templatesRegisterDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/templates/TemplatesRegister.vue'));
      addComponents({ component: componentModule, id: 'templatesRegisterDialog' });
      templatesRegisterDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      templatesRegisterDialog.resolve = resolve;
      templatesRegisterDialog.reject = reject;
      templatesRegisterDialog.options = options;
      templatesRegisterDialog.state = true;
    });
  };

  const closeTemplatesRegisterDialog = () => {
    templatesRegisterDialog.state = false;
  };

  const openButtonTemplateDialog = (options = {}) => {
    if (!buttonTemplateDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/button/ButtonDialog.vue'));
      addComponents({ component: componentModule, id: 'buttonTemplateDialog' });
      buttonTemplateDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      buttonTemplateDialog.resolve = resolve;
      buttonTemplateDialog.reject = reject;
      buttonTemplateDialog.options = options;
      buttonTemplateDialog.state = true;
    });
  };

  const closeButtonTemplateDialog = () => {
    buttonTemplateDialog.state = false;
  };

  const openFallbackMessageDialog = (options = {}) => {
    if (!fallbackMessageDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/fallback/FallbackMessage.vue'));
      addComponents({ component: componentModule, id: 'fallbackMessageDialog' });
      fallbackMessageDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      fallbackMessageDialog.resolve = resolve;
      fallbackMessageDialog.reject = reject;
      fallbackMessageDialog.options = options;
      fallbackMessageDialog.state = true;
    });
  };

  const closeFallbackMessageDialog = () => {
    fallbackMessageDialog.state = false;
  };

  const openCouponSettingDialog = (options = {}) => {
    if (!couponSettingDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/coupon/CouponSettingDialog.vue'));
      addComponents({ component: componentModule, id: 'couponSettingDialog' });
      couponSettingDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      couponSettingDialog.resolve = resolve;
      couponSettingDialog.reject = reject;
      couponSettingDialog.options = options;
      couponSettingDialog.state = true;
    });
  };

  const closeCouponSettingDialog = () => {
    couponSettingDialog.state = false;
  };

  const openCouponTemplatesDialog = (options = {}) => {
    if (!couponTemplatesDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/coupon/CouponTemplatesDialog.vue'));
      addComponents({ component: componentModule, id: 'couponTemplatesDialog' });
      couponTemplatesDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      couponTemplatesDialog.resolve = resolve;
      couponTemplatesDialog.reject = reject;
      couponTemplatesDialog.options = options;
      couponTemplatesDialog.state = true;
    });
  };

  const closeCouponTemplatesDialog = () => {
    couponTemplatesDialog.state = false;
  };

  const openCouponTemplateRegisterDialog = (options = {}) => {
    if (!couponTemplateRegisterDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/coupon/CouponTemplateRegisterDialog.vue'));
      addComponents({ component: componentModule, id: 'couponTemplateRegisterDialog' });
      couponTemplateRegisterDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      couponTemplateRegisterDialog.resolve = resolve;
      couponTemplateRegisterDialog.reject = reject;
      couponTemplateRegisterDialog.options = options;
      couponTemplateRegisterDialog.state = true;
    });
  };

  const closeCouponTemplateRegisterDialog = () => {
    couponTemplateRegisterDialog.state = false;
  };

  const openCarouselTailSettingDialog = (options = {}) => {
    if (!carouselTailSettingDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/CarouselTailSettingDialog.vue'));
      addComponents({ component: componentModule, id: 'couponTemplateRegisterDialog' });
      carouselTailSettingDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      carouselTailSettingDialog.resolve = resolve;
      carouselTailSettingDialog.reject = reject;
      carouselTailSettingDialog.options = options;
      carouselTailSettingDialog.state = true;
    });
  };

  const closeCarouselTailSettingDialog = () => {
    carouselTailSettingDialog.state = false;
  };

  const openAlimtalkTemplatesDialog = (options = {}) => {
    if (!alimtalkTemplatesDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/templates/AlimtalkTemplatesDialog.vue'));
      addComponents({ component: componentModule, id: 'alimtalkTemplatesDialog' });
      alimtalkTemplatesDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      alimtalkTemplatesDialog.resolve = resolve;
      alimtalkTemplatesDialog.reject = reject;
      alimtalkTemplatesDialog.options = options;
      alimtalkTemplatesDialog.state = true;
    });
  };

  const closeAlimtalkTemplatesDialog = () => {
    alimtalkTemplatesDialog.state = false;
  };

  const openAlimtalkButtonDialog = (options = {}) => {
    if (!alimtalkButtonDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/button/AlimtalkButtonDialog.vue'));
      addComponents({ component: componentModule, id: 'alimtalkButtonDialog' });
      alimtalkButtonDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      alimtalkButtonDialog.resolve = resolve;
      alimtalkButtonDialog.reject = reject;
      alimtalkButtonDialog.options = options;
      alimtalkButtonDialog.state = true;
    });
  };

  const closeAlimtalkButtonDialog = () => {
    alimtalkButtonDialog.state = false;
  };

  const openReservationDialog = (options = {}) => {
    if (!reservationDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ReservationDialog.vue'));
      addComponents({ component: componentModule, id: 'reservationDialog' });
      reservationDialog.init = true;
    }

    return new Promise((resolve, reject) => {
      reservationDialog.resolve = resolve;
      reservationDialog.reject = reject;
      reservationDialog.options = options;
      reservationDialog.state = true;
    });
  };

  const closeReservationDialog = () => {
    reservationDialog.state = false;
  };

  // const openDeliveryResult = (options = {}) => {
  //   if (!deliveryResult.init) {
  //     const componentModule =
  // defineAsyncComponent(() => import('@/components/dialog/DeliveryResult.vue'));
  //     addComponents({ component: componentModule, id: 'deliveryResult' });
  //     deliveryResult.init = true;
  //   }

  //   return new Promise((resolve, reject) => {
  //     deliveryResult.resolve = resolve;
  //     deliveryResult.reject = reject;
  //     deliveryResult.options = options;
  //     deliveryResult.state = true;
  //   });
  // };

  // const closeDeliveryResult = () => {
  //   deliveryResult.state = false;
  // };

  const openAdditionalFields = (options = {}) => {
    if (!additionalFields.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AdditionalFieldsDialog.vue'));
      addComponents({ component: componentModule, id: 'additionalFields' });
      additionalFields.init = true;
    }

    return new Promise((resolve, reject) => {
      additionalFields.resolve = resolve;
      additionalFields.reject = reject;
      additionalFields.options = options;
      additionalFields.state = true;
    });
  };

  const closeAdditionalFields = () => {
    additionalFields.state = false;
  };

  const openAdvertInfo = () => {
    if (!advertInfo.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AdvertInfoDialog.vue'));
      addComponents({ component: componentModule, id: 'advertInfo' });
      advertInfo.init = true;
    }

    return new Promise((resolve, reject) => {
      advertInfo.resolve = resolve;
      advertInfo.reject = reject;
      advertInfo.state = true;
    });
  };

  const closeAdvertInfo = () => {
    advertInfo.state = false;
  };

  const openAdvertWarning = () => {
    if (advertWarning.init) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 0);
      });
    }

    if (!advertWarning.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AdvertWarningDialog.vue'));
      addComponents({ component: componentModule, id: 'advertWarning' });
      advertWarning.init = true;
    }

    return new Promise((resolve, reject) => {
      advertWarning.resolve = resolve;
      advertWarning.reject = reject;
      advertWarning.state = true;
    });
  };

  const closeTargetingSettings = () => {
    targetingSettings.state = false;
  };

  const openTargetingSettings = () => {
    if (!targetingSettings.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/TargetingSettingsDialog.vue'));
      addComponents({ component: componentModule, id: 'targetingSettings' });
      targetingSettings.init = true;
    }

    return new Promise((resolve, reject) => {
      targetingSettings.resolve = resolve;
      targetingSettings.reject = reject;
      targetingSettings.state = true;
    });
  };

  const closeAdvertWarning = () => {
    advertWarning.state = false;
  };

  const openTemplatePreview = (options = {}) => {
    if (!templatePreview.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/templates/TemplatePreviewDialog.vue'));
      addComponents({ component: componentModule, id: 'templatePreview' });
      templatePreview.init = true;
    }

    return new Promise((resolve, reject) => {
      templatePreview.resolve = resolve;
      templatePreview.reject = reject;
      templatePreview.options = options;
      templatePreview.state = true;
    });
  };

  const closeTemplatePreview = () => {
    templatePreview.state = false;
  };

  const openBrandTemplates = (options = {}) => {
    if (!brandTemplates.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/brand/dialog/TemplatesRegister.vue'));
      addComponents({
        component: componentModule,
        id: 'brandTemplates',
      });
      brandTemplates.init = true;
    }

    return new Promise((resolve, reject) => {
      brandTemplates.resolve = resolve;
      brandTemplates.reject = reject;
      brandTemplates.options = options;
      brandTemplates.state = true;
    });
  };

  const closeBrandTemplates = () => {
    brandTemplates.state = false;
  };

  return {
    components,
    addComponents,
    removeComponent,
    openAlertDialog,
    closeAlertDialog,
    alert,

    // 이미지 리스트
    imageListDialog,
    openImageListDialog,
    closeImageListDialog,

    // 이미지 생성
    imageRegisterDialog,
    openImageRegisterDialog,
    closeImageRegisterDialog,

    // 템플릿 리스트
    templatesDialog,
    openTemplatesDialog,
    closeTemplatesDialog,

    // 템플릿 생성
    templatesRegisterDialog,
    openTemplatesRegisterDialog,
    closeTemplatesRegisterDialog,

    // 버튼
    buttonTemplateDialog,
    openButtonTemplateDialog,
    closeButtonTemplateDialog,

    // 대체발송
    fallbackMessageDialog,
    openFallbackMessageDialog,
    closeFallbackMessageDialog,

    // 쿠폰 설정
    couponSettingDialog,
    openCouponSettingDialog,
    closeCouponSettingDialog,

    // 쿠폰 템플릿
    couponTemplatesDialog,
    openCouponTemplatesDialog,
    closeCouponTemplatesDialog,

    // 쿠폰 등록
    couponTemplateRegisterDialog,
    openCouponTemplateRegisterDialog,
    closeCouponTemplateRegisterDialog,

    // 캐로셀 더보기 설정
    carouselTailSettingDialog,
    openCarouselTailSettingDialog,
    closeCarouselTailSettingDialog,

    // 알림톡 템플릿
    alimtalkTemplatesDialog,
    openAlimtalkTemplatesDialog,
    closeAlimtalkTemplatesDialog,

    // 알림톡 버튼
    alimtalkButtonDialog,
    openAlimtalkButtonDialog,
    closeAlimtalkButtonDialog,

    // 예약현황
    reservationDialog,
    openReservationDialog,
    closeReservationDialog,

    // 발송결과
    // deliveryResult,
    // openDeliveryResult,
    // closeDeliveryResult,

    // additionalFields
    additionalFields,
    openAdditionalFields,
    closeAdditionalFields,

    advertInfo,
    openAdvertInfo,
    closeAdvertInfo,

    advertWarning,
    openAdvertWarning,
    closeAdvertWarning,

    templatePreview,
    openTemplatePreview,
    closeTemplatePreview,

    confirm,
    openConfirmDialog,
    closeConfirmDialog,

    targetingSettings,
    openTargetingSettings,
    closeTargetingSettings,

    brandTemplates,
    openBrandTemplates,
    closeBrandTemplates,
  };
});
